import React from "react";
import { DayPicker, useNavigation } from "react-day-picker";
import { createPortal } from "react-dom";
import getAvatarUrl from "../../../utils/get-avatar-url";
import {
  BLOCK_DATE_RANGE,
  BLOCK_DATE_SINGLE,
  buildBlockTimeBlackoutPayloads,
  submitBlockTimePayloads,
} from "./CreateBlockTimeModal";

const POPOVER_EST_HEIGHT = 320;
const POPOVER_EST_WIDTH = 296;

function calendarDateToYyyyMmDd(d) {
  if (!d || !(d instanceof Date) || Number.isNaN(d.getTime())) return "";
  const uy = d.getUTCFullYear();
  const um = d.getUTCMonth();
  const ud = d.getUTCDate();
  const utcMidnightMs = Date.UTC(uy, um, ud);
  const isExactlyUtcMidnight =
    d.getTime() === utcMidnightMs &&
    d.getUTCHours() === 0 &&
    d.getUTCMinutes() === 0 &&
    d.getUTCSeconds() === 0 &&
    d.getUTCMilliseconds() === 0;
  if (isExactlyUtcMidnight) {
    return `${uy}-${String(um + 1).padStart(2, "0")}-${String(ud).padStart(2, "0")}`;
  }
  const y = d.getFullYear();
  const m = d.getMonth() + 1;
  const day = d.getDate();
  return `${y}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function dateAtNoonFromYyyyMmDd(ymd) {
  if (!ymd || typeof ymd !== "string") return undefined;
  const m = ymd.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return undefined;
  return new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]), 12, 0, 0, 0);
}

function normalizeCalendarDateForPicker(d) {
  const ymd = calendarDateToYyyyMmDd(d);
  if (!ymd) return d;
  return dateAtNoonFromYyyyMmDd(ymd);
}

function startOfLocalDayMs(d) {
  if (!d || !(d instanceof Date)) return NaN;
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x.getTime();
}

function formatDate(d) {
  return d
    ? d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })
    : "";
}

function formatTime(d) {
  if (!(d instanceof Date)) return "";
  return d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true }).toLowerCase().replace(" ", "");
}

function formatTimeRange(start, end) {
  if (!(start instanceof Date)) return "";
  const a = formatTime(start);
  if (!(end instanceof Date) || Number.isNaN(end.getTime())) return a;
  return `${a} - ${formatTime(end)}`;
}

function extractCoaches(raw, theme) {
  if (!raw) return [];
  const list = Array.isArray(raw.coaches) ? raw.coaches : [];
  const seen = new Set();
  const out = [];
  for (const entry of list) {
    const c = entry?.coachId ?? entry?.coach ?? entry;
    if (!c || typeof c !== "object") continue;
    const id = String(c._id ?? c.id ?? "");
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push({
      id,
      name: String(c.name ?? "").trim() || "Coach",
      avatarUrl: getAvatarUrl(c, false, theme, theme !== "dark"),
    });
  }
  return out;
}

function BlockTimeCalendarCaption(props) {
  const { goToMonth, nextMonth, previousMonth } = useNavigation();
  const displayMonth = props?.displayMonth ?? new Date();
  const monthLabel = displayMonth.toLocaleDateString("en-US", { month: "long", year: "numeric" });
  return (
    <div className="create-class-modal-calendar-caption">
      <button
        type="button"
        onClick={() => previousMonth && goToMonth(previousMonth)}
        className="create-class-modal-calendar-nav"
        aria-label="Previous month"
      >
        <img src="/img/new-icons/arrow-left.svg" alt="" />
      </button>
      <span className="create-class-modal-calendar-title">{monthLabel}</span>
      <button
        type="button"
        onClick={() => nextMonth && goToMonth(nextMonth)}
        className="create-class-modal-calendar-nav"
        aria-label="Next month"
      >
        <img src="/img/new-icons/arrow-right.svg" alt="" />
      </button>
    </div>
  );
}

const BlockTimeFromDetailsModal = ({ isOpen, event, theme, onClose, onConfirm }) => {
  const [dateMode, setDateMode] = React.useState(BLOCK_DATE_SINGLE);
  const [singleDayDate, setSingleDayDate] = React.useState(undefined);
  const [rangeStartDate, setRangeStartDate] = React.useState(undefined);
  const [rangeEndDate, setRangeEndDate] = React.useState(undefined);
  const [openDatePicker, setOpenDatePicker] = React.useState(null);
  const [calendarPopoverPos, setCalendarPopoverPos] = React.useState({ top: 0, left: 0 });
  const [submitting, setSubmitting] = React.useState(false);
  const [error, setError] = React.useState(null);

  const singlePickerRef = React.useRef(null);
  const rangeStartPickerRef = React.useRef(null);
  const rangeEndPickerRef = React.useRef(null);
  const calendarPopoverRef = React.useRef(null);

  React.useEffect(() => {
    if (isOpen) {
      setDateMode(BLOCK_DATE_SINGLE);
      setSingleDayDate(undefined);
      setRangeStartDate(undefined);
      setRangeEndDate(undefined);
      setOpenDatePicker(null);
      setError(null);
      setSubmitting(false);
    }
  }, [isOpen]);

  const getPickerRefForKey = React.useCallback((key) => {
    if (key === "blockSingle") return singlePickerRef;
    if (key === "blockRangeStart") return rangeStartPickerRef;
    if (key === "blockRangeEnd") return rangeEndPickerRef;
    return null;
  }, []);

  const updateCalendarPopoverPosition = React.useCallback(() => {
    if (!openDatePicker) return;
    const anchorRef = getPickerRefForKey(openDatePicker);
    if (!anchorRef?.current) return;
    const rect = anchorRef.current.getBoundingClientRect();
    const gap = 4;
    const pad = 8;
    const left = Math.max(pad, Math.min(rect.left, window.innerWidth - POPOVER_EST_WIDTH - pad));
    let top = rect.bottom + gap;
    if (rect.bottom + POPOVER_EST_HEIGHT > window.innerHeight - pad && rect.top > POPOVER_EST_HEIGHT + pad) {
      top = rect.top - POPOVER_EST_HEIGHT - gap;
    }
    setCalendarPopoverPos({ top, left });
  }, [openDatePicker, getPickerRefForKey]);

  React.useLayoutEffect(() => {
    if (!openDatePicker) return;
    updateCalendarPopoverPosition();
  }, [openDatePicker, updateCalendarPopoverPosition]);

  React.useEffect(() => {
    if (!openDatePicker) return;
    const onScrollOrResize = () => updateCalendarPopoverPosition();
    window.addEventListener("scroll", onScrollOrResize, true);
    window.addEventListener("resize", onScrollOrResize);
    return () => {
      window.removeEventListener("scroll", onScrollOrResize, true);
      window.removeEventListener("resize", onScrollOrResize);
    };
  }, [openDatePicker, updateCalendarPopoverPosition]);

  React.useEffect(() => {
    if (!openDatePicker) return;
    const handleClickOutside = (e) => {
      const anchorRef = getPickerRefForKey(openDatePicker);
      const inAnchor = anchorRef?.current?.contains(e.target);
      const inPopover = calendarPopoverRef.current?.contains(e.target);
      if (!inAnchor && !inPopover) setOpenDatePicker(null);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [openDatePicker, getPickerRefForKey]);

  if (!isOpen || !event) return null;

  const raw = event.raw ?? {};
  const eventId = String(raw._id ?? raw.id ?? "");
  const coaches = extractCoaches(raw, theme);

  const recurrenceLabel = (() => {
    const r = raw.recurrence_type ?? raw.schedule_type;
    const map = { one_time: "One-time", daily: "Daily", weekly: "Weekly", monthly: "Monthly", custom: "Custom" };
    return map[r] ?? r ?? null;
  })();

  const thumbnail =
    typeof raw.thumbnail_url === "string" && raw.thumbnail_url
      ? raw.thumbnail_url
      : event.kind === "facility"
        ? "/img/new-icons/facilities-icon.svg"
        : event.kind === "pt"
          ? "/img/new-icons/pt-session-icon.svg"
          : "/img/new-icons/classes-icon.svg";

  const dateSelectionValid = dateMode === BLOCK_DATE_SINGLE
    ? Boolean(singleDayDate)
    : Boolean(rangeStartDate) && Boolean(rangeEndDate) && startOfLocalDayMs(rangeStartDate) <= startOfLocalDayMs(rangeEndDate);

  const canSubmit = Boolean(eventId) && dateSelectionValid;

  const showSummaryBanner = Boolean(event.title) && dateSelectionValid;

  const calendarSelectedDate =
    openDatePicker === "blockSingle"
      ? singleDayDate
      : openDatePicker === "blockRangeStart"
        ? rangeStartDate
        : openDatePicker === "blockRangeEnd"
          ? rangeEndDate
          : undefined;

  const calendarPortal =
    openDatePicker &&
    typeof document !== "undefined" &&
    createPortal(
      <div
        ref={calendarPopoverRef}
        className="create-class-modal-date-popover create-blackout-date-popover-portal"
        style={{ position: "fixed", top: calendarPopoverPos.top, left: calendarPopoverPos.left, zIndex: 9999 }}
      >
        <DayPicker
          mode="single"
          selected={calendarSelectedDate}
          onSelect={(next) => {
            const norm = next ? normalizeCalendarDateForPicker(next) : next;
            if (openDatePicker === "blockSingle") setSingleDayDate(norm);
            else if (openDatePicker === "blockRangeStart") setRangeStartDate(norm);
            else if (openDatePicker === "blockRangeEnd") setRangeEndDate(norm);
            setOpenDatePicker(null);
          }}
          defaultMonth={calendarSelectedDate || new Date()}
          showOutsideDays
          components={{ Caption: BlockTimeCalendarCaption }}
          modifiersClassNames={{ selected: "my-selected" }}
        />
      </div>,
      document.body
    );

  const renderDateField = (id, label, date, pickerKey) => (
    <div className="form-group">
      <span className="create-blackout-date-field-caption">{label}</span>
      <div
        className="create-class-modal-date-dropdown create-blackout-date-field create-block-time-date-dropdown"
        ref={getPickerRefForKey(pickerKey)}
      >
        <input type="hidden" name={id} value={calendarDateToYyyyMmDd(date)} />
        <img
          src="/img/new-icons/calendar-ico.svg"
          alt=""
          className="create-class-modal-date-icon"
          aria-hidden
        />
        <button
          type="button"
          id={id}
          className="create-class-modal-date-input create-class-modal-date-trigger create-block-time-date-trigger"
          onClick={() => setOpenDatePicker((prev) => (prev === pickerKey ? null : pickerKey))}
          aria-label={label}
          aria-expanded={openDatePicker === pickerKey}
        >
          {formatDate(date) || "Select"}
        </button>
        <svg
          className="create-class-modal-select-chevron"
          xmlns="http://www.w3.org/2000/svg"
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          aria-hidden="true"
        >
          <path stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" strokeWidth="1.5" d="M6 9l6 6 6-6"/>
        </svg>
      </div>
    </div>
  );

  const handleSubmit = async () => {
    if (!canSubmit || submitting) return;
    setError(null);
    setSubmitting(true);
    try {
      const payloads = buildBlockTimeBlackoutPayloads({
        bookableIds: [eventId],
        dateMode,
        singleDayDate,
        rangeStartDate,
        rangeEndDate,
      });
      await submitBlockTimePayloads(payloads);
      onClose();
      onConfirm?.();
    } catch (err) {
      setError(err?.message || "Failed to block time.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
    {createPortal(
      <>
      <button type="button" className="block-details-modal-overlay" onClick={onClose} aria-label="Close modal" />
      <div className="block-details-modal">
        {/* Header */}
        <div className="block-details-modal-header">
          <h2 className="block-details-modal-title">Block time</h2>
          <button type="button" className="block-details-modal-close" onClick={onClose} aria-label="Close">
            <img src="/img/new-icons/add-square-scheduling.svg" alt="" />
          </button>
        </div>

        {/* Event summary */}
        <div className="block-details-modal-event-section">
          <div className="scheduling-class-card-thumb">
            <img
              src={thumbnail}
              alt=""
              className={typeof raw.thumbnail_url === "string" && raw.thumbnail_url ? "scheduling-class-thumb-photo" : "scheduling-class-thumb-icon"}
            />
          </div>
          <div className="scheduling-class-card-main">
            <h3 className="scheduling-class-card-title">{event.title}</h3>
            {raw.description && <p className="scheduling-class-card-desc">{raw.description}</p>}
            <div className="scheduling-class-card-info">
              <div className="scheduling-class-card-details">
                {/* Row 1: date, duration, location */}
                {event.start instanceof Date && (
                  <span title="Date">
                    <img src="/img/new-icons/calendar-ico.svg" alt="" />
                    {event.start.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                  </span>
                )}
                {raw.duration_minutes != null && (
                  <span title="Duration">
                    <img src="/img/new-icons/vuesax-linear-timer-pause.svg" alt="" />
                    {raw.duration_minutes < 60 ? `${raw.duration_minutes} mins` : `${Math.floor(raw.duration_minutes / 60)}h${raw.duration_minutes % 60 ? ` ${raw.duration_minutes % 60}m` : ""}`}
                  </span>
                )}
                {raw.location && (
                  <span title="Location">
                    <img src="/img/new-icons/push-pin.svg" alt="" />
                    {raw.location}
                  </span>
                )}
                {/* Row 2: time, recurrence, capacity */}
                {event.start instanceof Date && (
                  <span title="Time">
                    <img src="/img/new-icons/vuesax-linear-clock.svg" alt="" />
                    {formatTimeRange(event.start, event.end)}
                  </span>
                )}
                {recurrenceLabel && (
                  <span title="Recurrence">
                    <img src="/img/new-icons/vuesax-linear-refresh-2.svg" alt="" />
                    {recurrenceLabel}
                  </span>
                )}
                {raw.capacity != null && (
                  <span title="Capacity">
                    <img src="/img/new-icons/team-member.svg" alt="" />
                    {raw.capacity} {Number(raw.capacity) === 1 ? "Seat" : "Seats"}
                  </span>
                )}
              </div>
              {coaches.length > 0 && (
                <div className="scheduling-class-card-coaches block-details-modal-coaches">
                  <span className="scheduling-class-card-coaches-label">Coaches:</span>
                  <span className="scheduling-class-card-avatar-stack">
                    {coaches.slice(0, 3).map((c) => (
                      <span key={c.id} className="scheduling-class-card-avatar" title={c.name}>
                        <img src={c.avatarUrl} alt="" />
                      </span>
                    ))}
                    {coaches.length > 3 && (
                      <span className="scheduling-class-card-avatar-extra-pill">+{coaches.length - 3}</span>
                    )}
                  </span>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="block-details-modal-divider" />

        {/* Select Date — same layout/classes as CreateBlockTimeModal */}
        <div className="block-details-modal-body create-class-modal-cols">
          <div className="create-class-modal-section-head">
            <h3 className="create-class-modal-section-title">Select Date</h3>
            <p className="create-class-modal-section-desc">
              Choose whether this block applies to a single date or multiple dates.
            </p>
          </div>
          <div className="create-class-modal-form">
            <fieldset className="create-blackout-schedule-card">
              <legend className="sr-only">Date scope</legend>
              <div className="create-blackout-schedule-option">
                <label className="create-blackout-schedule-option-row radio-input">
                  <input
                    type="radio"
                    name="block-details-date-mode"
                    value={BLOCK_DATE_SINGLE}
                    checked={dateMode === BLOCK_DATE_SINGLE}
                    onChange={() => setDateMode(BLOCK_DATE_SINGLE)}
                  />
                  <span className="radio" aria-hidden="true" />
                  <span className="create-blackout-schedule-option-copy">
                    <span className="create-blackout-schedule-option-title">Single Day</span>
                    <span className="create-blackout-schedule-option-desc">Block bookings for one specific date.</span>
                  </span>
                </label>
                {dateMode === BLOCK_DATE_SINGLE && (
                  <div className="create-blackout-schedule-nested">
                    {renderDateField("block-details-single-date", "Select Date", singleDayDate, "blockSingle")}
                  </div>
                )}
              </div>
              <div className="create-blackout-schedule-option">
                <label className="create-blackout-schedule-option-row radio-input">
                  <input
                    type="radio"
                    name="block-details-date-mode"
                    value={BLOCK_DATE_RANGE}
                    checked={dateMode === BLOCK_DATE_RANGE}
                    onChange={() => setDateMode(BLOCK_DATE_RANGE)}
                  />
                  <span className="radio" aria-hidden="true" />
                  <span className="create-blackout-schedule-option-copy">
                    <span className="create-blackout-schedule-option-title">Date Range</span>
                    <span className="create-blackout-schedule-option-desc">Block bookings across multiple consecutive dates.</span>
                  </span>
                </label>
                {dateMode === BLOCK_DATE_RANGE && (
                  <div className="create-blackout-schedule-nested">
                    <div className="create-class-modal-form-two-cols">
                      {renderDateField("block-details-range-start", "Start date", rangeStartDate, "blockRangeStart")}
                      {renderDateField("block-details-range-end", "End date", rangeEndDate, "blockRangeEnd")}
                    </div>
                  </div>
                )}
              </div>
            </fieldset>
          </div>
        </div>

        {/* Warning banner */}
        {showSummaryBanner && (
          <div className="create-blackout-summary-warning create-block-time-date-banner" aria-live="polite">
            <img
              src="/img/new-icons/info-circle-red.svg"
              alt=""
              className="create-blackout-summary-warning-icon"
              aria-hidden
            />
            <p className="create-blackout-summary-warning-text">
              This block will prevent bookings for{" "}
              <strong className="create-blackout-summary-strong">{event.title}</strong>
              {dateMode === BLOCK_DATE_SINGLE ? (
                <> on <strong className="create-blackout-summary-strong">{formatDate(singleDayDate)}</strong>.</>
              ) : (
                <> from <strong className="create-blackout-summary-strong">{formatDate(rangeStartDate)}</strong> to <strong className="create-blackout-summary-strong">{formatDate(rangeEndDate)}</strong>.</>
              )}
            </p>
          </div>
        )}

        {error && <p className="create-class-modal-error" role="alert">{error}</p>}

        {/* Footer */}
        <footer className="scheduling-classes-modal-footer">
          <button type="button" data-button-type="outline" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            data-button-type="primary"
            onClick={handleSubmit}
            disabled={!canSubmit || submitting}
          >
            {submitting ? "Blocking…" : "Block Time"}
          </button>
        </footer>
      </div>
      </>,
      document.body
    )}
    {calendarPortal}
    </>
  );
};

export default BlockTimeFromDetailsModal;
